import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { ArrowLeft, Loader2, Trophy, Target, Trash2, CheckCircle } from 'lucide-react';

export default function FanGameSummary() {
  const { gameId } = useParams();
  const navigate = useNavigate();
  const [game, setGame] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchGame();
    fetchEvents();
  }, [gameId]);

  const fetchGame = async () => {
    try {
      const response = await api.get(`/fan/games/${gameId}`);
      setGame(response.data);
    } catch (error) {
      toast.error('Failed to load game');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const fetchEvents = async () => {
    try {
      const response = await api.get(`/fan/games/${gameId}/events`);
      setEvents(response.data || []);
    } catch (error) {
      console.error('Failed to fetch events:', error);
    }
  };

  const handleDeleteGame = async () => {
    if (!confirm('Delete this game? This cannot be undone.')) return;
    try {
      await api.delete(`/fan/games/${gameId}`);
      toast.success('Game deleted');
      navigate('/dashboard');
    } catch (error) {
      toast.error('Failed to delete game');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (!game) return null;

  const playerName = game.player?.name || 'Player';
  const playerTeam = game.player?.team_name || 'Team';
  const playerJersey = game.player?.jersey_number || '?';
  const playerScore = game.is_home ? game.home_score : game.away_score;
  const opponentScore = game.is_home ? game.away_score : game.home_score;
  const won = playerScore > opponentScore;
  const stats = calculateDetailedStats(events);

  return (
    <div
      className="h-full overflow-y-auto bg-slate-50 pb-app-nav"
      style={{ WebkitOverflowScrolling: 'touch', paddingBottom: 'calc(env(safe-area-inset-bottom) + 96px)' }}
    >
      {/* Header — three-region: left (back), center (score), right (delete) */}
      <div className="sticky top-0 z-10" style={{ backgroundColor: '#13314b' }}>
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center relative" style={{ minHeight: '120px' }}>
            {/* Left: Dashboard button */}
            <div className="flex-1 flex items-center z-10">
              <Button
                onClick={() => navigate('/dashboard')}
                className="h-10 px-4 font-bold hover:opacity-90"
                style={{ backgroundColor: '#ffa133', color: '#13314b' }}
                data-testid="back-to-dashboard"
              >
                <ArrowLeft className="w-5 h-5 mr-2" />
                Dashboard
              </Button>
            </div>

            {/* Center: Score block — absolutely centered */}
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
              <div className="text-xs text-white/50 uppercase font-bold tracking-wider mb-3">
                {new Date(game.date_time).toLocaleDateString('en-US', {
                  weekday: 'short', month: 'short', day: 'numeric', year: 'numeric'
                })}
              </div>
              <div className="flex items-center gap-10">
                <div className="text-center min-w-[120px]">
                  <p className="text-xs font-bold uppercase tracking-widest text-white/60 leading-tight mb-2">{playerTeam}</p>
                  <p className="text-5xl font-bold text-white tabular-nums leading-none">{playerScore}</p>
                </div>
                <div className="text-3xl font-bold text-white/30 select-none">–</div>
                <div className="text-center min-w-[120px]">
                  <p className="text-xs font-bold uppercase tracking-widest text-white/60 leading-tight mb-2">{game.opponent_name}</p>
                  <p className="text-5xl font-bold text-white tabular-nums leading-none">{opponentScore}</p>
                </div>
              </div>
              <div className="mt-4 pointer-events-auto">
                <span className="inline-flex items-center px-3 py-0.5 rounded-full font-bold text-xs uppercase tracking-wide" style={{ backgroundColor: won ? '#16A085' : '#C0392B', color: '#fff' }}>
                  <CheckCircle className="w-3 h-3 mr-1" /> {won ? 'Win' : 'Loss'} — Final
                </span>
              </div>
            </div>

            {/* Right: Delete button */}
            <div className="flex-1 flex items-center justify-end z-10">
              <Button
                onClick={handleDeleteGame}
                className="h-10 px-4 font-bold text-white hover:opacity-90"
                style={{ backgroundColor: '#C0392B' }}
                data-testid="delete-game-button"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto p-4 space-y-6">
        {/* Player Header */}
        <div className="overflow-hidden" style={{ border: '2px solid #FFFFFF', borderRadius: '5px' }}>
          <div className="p-4" style={{ backgroundColor: '#13314b' }}>
            <div className="flex items-center space-x-4">
              <div className="w-14 h-14 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(255,255,255,0.15)' }}>
                <span className="text-xl font-bold text-white">#{playerJersey}</span>
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">{playerName}</h2>
                <p className="text-sm text-white/60">{playerTeam} {game.player?.season ? `• ${game.player.season}` : ''}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="overflow-hidden" style={{ border: '2px solid #FFFFFF', borderRadius: '5px' }}>
          <div className="p-4" style={{ backgroundColor: '#13314b' }}>
            <h3 className="font-bold text-xl uppercase tracking-tight text-white text-center flex items-center justify-center">
              <Target className="w-5 h-5 mr-2" />
              Game Stats
            </h3>
          </div>
          <div className="p-6 bg-white">
            {/* Scoring */}
            <div className="mb-6">
              <h4 className="text-xs text-slate-500 uppercase tracking-wide mb-3">Scoring</h4>
              <div className="grid grid-cols-4 gap-4">
                <StatDisplay label="PTS" value={stats.pts} large highlight />
                <StatDisplay label="FG" value={`${stats.fgm}-${stats.fga}`} pct={stats.fg_pct} />
                <StatDisplay label="3PT" value={`${stats.three_pm}-${stats.three_pa}`} pct={stats.three_pct} />
                <StatDisplay label="FT" value={`${stats.ftm}-${stats.fta}`} pct={stats.ft_pct} />
              </div>
            </div>

            {/* Other Stats */}
            <div>
              <h4 className="text-xs text-slate-500 uppercase tracking-wide mb-3">Other</h4>
              <div className="grid grid-cols-6 gap-3">
                <StatDisplay label="REB" value={stats.reb} />
                <StatDisplay label="AST" value={stats.ast} />
                <StatDisplay label="STL" value={stats.stl} />
                <StatDisplay label="BLK" value={stats.blk} />
                <StatDisplay label="TO" value={stats.to} negative />
                <StatDisplay label="PF" value={stats.pf} />
              </div>
            </div>

            {/* Rebound Breakdown */}
            <div className="mt-4 pt-4 border-t border-slate-100">
              <div className="flex gap-6 text-sm">
                <span className="text-slate-500">Off Rebounds: <strong className="text-slate-900">{stats.oreb}</strong></span>
                <span className="text-slate-500">Def Rebounds: <strong className="text-slate-900">{stats.dreb}</strong></span>
                <span className="text-slate-500">Deflections: <strong className="text-slate-900">{stats.defl}</strong></span>
              </div>
            </div>
          </div>
        </div>

        {/* Play by Play */}
        <div className="overflow-hidden" style={{ border: '2px solid #FFFFFF', borderRadius: '5px' }}>
          <div className="p-4" style={{ backgroundColor: '#13314b' }}>
            <h3 className="font-bold text-xl uppercase tracking-tight text-white text-center flex items-center justify-center">
              <Trophy className="w-5 h-5 mr-2" />
              Play by Play ({events.length} events)
            </h3>
          </div>
          <div className="max-h-80 overflow-y-auto bg-white">
            {events.length === 0 ? (
              <div className="p-8 text-center text-slate-500">No events recorded</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-slate-100 sticky top-0 border-b border-slate-200">
                  <tr>
                    <th className="text-left px-3 py-2 font-bold text-xs uppercase text-slate-600">Period</th>
                    <th className="text-left px-3 py-2 font-bold text-xs uppercase text-slate-600">Action</th>
                    <th className="text-right px-3 py-2 font-bold text-xs uppercase text-slate-600">Points</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {events.map((event, idx) => (
                    <tr key={event.event_id || idx} className="hover:bg-slate-50">
                      <td className="px-3 py-2 font-bold text-slate-600">Q{event.period}</td>
                      <td className="px-3 py-2 text-slate-900">{formatEventType(event.event_type, event.sub_type)}</td>
                      <td className="px-3 py-2 text-right">
                        {event.points > 0 && (
                          <span className="text-green-600 font-bold">+{event.points}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function StatDisplay({ label, value, pct, large, highlight, negative }) {
  return (
    <div className={`text-center p-3 rounded-lg ${highlight ? 'bg-slate-100' : 'bg-slate-50'}`} style={highlight ? { border: '1px solid #e2e8f0' } : {}}>
      <p className={`font-bold ${large ? 'text-3xl' : 'text-xl'} ${
        highlight ? 'text-slate-900' : negative ? 'text-orange-600' : 'text-slate-900'
      }`}>
        {value}
      </p>
      {pct !== undefined && (
        <p className={`text-xs ${
          pct >= 50 ? 'text-green-600' : pct >= 40 ? 'text-amber-600' : 'text-red-500'
        }`}>
          {pct}%
        </p>
      )}
      <p className="text-xs text-slate-500 uppercase font-medium">{label}</p>
    </div>
  );
}

function calculateDetailedStats(events) {
  const stats = {
    pts: 0, fgm: 0, fga: 0, three_pm: 0, three_pa: 0,
    ftm: 0, fta: 0, oreb: 0, dreb: 0, reb: 0,
    ast: 0, stl: 0, blk: 0, defl: 0, to: 0, pf: 0,
    fg_pct: 0, three_pct: 0, ft_pct: 0
  };

  for (const event of events) {
    switch (event.event_type) {
      case 'shot_make':
        stats.pts += event.points || 0;
        if (event.sub_type === '3pt') { stats.three_pm += 1; stats.three_pa += 1; stats.fgm += 1; stats.fga += 1; }
        else if (event.sub_type === '2pt') { stats.fgm += 1; stats.fga += 1; }
        else if (event.sub_type === 'ft') { stats.ftm += 1; stats.fta += 1; }
        break;
      case 'shot_miss':
        if (event.sub_type === '3pt') { stats.three_pa += 1; stats.fga += 1; }
        else if (event.sub_type === '2pt') { stats.fga += 1; }
        else if (event.sub_type === 'ft') { stats.fta += 1; }
        break;
      case 'rebound':
        if (event.sub_type === 'offensive') stats.oreb += 1; else stats.dreb += 1;
        stats.reb += 1; break;
      case 'assist': stats.ast += 1; break;
      case 'steal': stats.stl += 1; break;
      case 'block': stats.blk += 1; break;
      case 'deflection': stats.defl += 1; break;
      case 'turnover': stats.to += 1; break;
      case 'foul': stats.pf += 1; break;
    }
  }

  if (stats.fga > 0) stats.fg_pct = Math.round((stats.fgm / stats.fga) * 100);
  if (stats.three_pa > 0) stats.three_pct = Math.round((stats.three_pm / stats.three_pa) * 100);
  if (stats.fta > 0) stats.ft_pct = Math.round((stats.ftm / stats.fta) * 100);

  return stats;
}

function formatEventType(type, subType) {
  const labels = {
    shot_make: subType ? `${subType.toUpperCase()} Made` : 'Shot Made',
    shot_miss: subType ? `${subType.toUpperCase()} Missed` : 'Shot Missed',
    rebound: subType === 'offensive' ? 'Offensive Rebound' : 'Defensive Rebound',
    assist: 'Assist', steal: 'Steal', block: 'Block',
    deflection: 'Deflection', turnover: 'Turnover', foul: 'Foul'
  };
  return labels[type] || type;
}
